import AddTxnStep1Icon from 'components/common/Icons/ver2/form-add-transaction/AddTxnStep1Icon';
import AddTxnStep2Icon from 'components/common/Icons/ver2/form-add-transaction/AddTxnStep2Icon';
import AddTxnStep3Icon from 'components/common/Icons/ver2/form-add-transaction/AddTxnStep3Icon';
import AddTxnStep4Icon from 'components/common/Icons/ver2/form-add-transaction/AddTxnStep4Icon';
import { getItemsConfig } from 'components/common/func';
import {
  ADD_TRANSACTION_PAYMENT_METHOD_NAME,
  ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME,
  ADD_TRANSACTION_YOU_SEND_NAME,
  METHOD_DEPOSIT,
  METHOD_EPAYMENT,
  METHOD_PAYID,
  METHOD_PAYTO,
  METHOD_POLI,
  SURCHARGE_RATE,
  VOUCHER,
} from 'constants';
import {
  PAY_BY_PAYTO_AMOUNT_MAX_LIMIT,
  PAY_BY_PAYTO_AMOUNT_MIN_LIMIT,
} from 'features/to/const';
import useLang from 'hooks/useLang';
import { numberUtils } from 'utils';
import useFormAddTransactionStore from './useFormAddTransactionStore';

export const MAKE_PAYMENT_KEY = 'isMakePayment';
export const ADD_TRANSACTION_TOTAL_NUMBER_OF_STEPS = 4;

const useAddTransaction = (countryConfig) => {
  const { t } = useLang();
  const { remittance, paymentMethod } = useFormAddTransactionStore();

  const discountApplied = paymentMethod?.discountApplied;
  const { discountAmount, totalAmountAfterDiscount } = discountApplied || {};
  const isDiscountApplied = Boolean(Number(discountAmount));

  const payLimitConfig =
    countryConfig &&
    getItemsConfig(countryConfig?.payLimits, countryConfig?.countryCode);
  const getPayLimitRanger = (payLimitConfig) => ({
    [METHOD_PAYID]: {
      payLimitMin: payLimitConfig?.['nppMin'] || 20,
      payLimitMax: payLimitConfig?.['nppMax'] || null,
      feesIncluded: payLimitConfig?.['nppFeesIncluded'] || false,
    },
    [METHOD_POLI]: {
      payLimitMin: payLimitConfig?.['poliMin'] || 20,
      payLimitMax: payLimitConfig?.['poliMax'] || null,
      feesIncluded: payLimitConfig?.['poliFeesIncluded'] || false,
    },
    [METHOD_DEPOSIT]: {
      payLimitMin: payLimitConfig?.['depositMin'] || 20,
      payLimitMax: payLimitConfig?.['depositMax'] || null,
      feesIncluded: payLimitConfig?.['depositFeesIncluded'] || false,
    },
    [METHOD_EPAYMENT]: {
      payLimitMin: payLimitConfig?.['epaymentMin'] || 20,
      payLimitMax: payLimitConfig?.['epaymentMax'] || null,
      feesIncluded: payLimitConfig?.['epaymentFeesIncluded'] || false,
    },
    [METHOD_PAYTO]: {
      payLimitMin:
        payLimitConfig?.['payToMin'] || PAY_BY_PAYTO_AMOUNT_MIN_LIMIT,
      payLimitMax:
        payLimitConfig?.['payToMax'] || PAY_BY_PAYTO_AMOUNT_MAX_LIMIT,
      feesIncluded: payLimitConfig?.['payToFeesIncluded'] || false,
    },
  });
  const isPaymentMethodDisabled = (paymentMethod) => {
    const payLimit = getPayLimitRanger(payLimitConfig)[paymentMethod];

    const sendAmount = numberUtils.mathRound(
      remittance[ADD_TRANSACTION_YOU_SEND_NAME] || 0,
    );
    const sendAnotherAmount = numberUtils.mathRound(
      remittance[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0,
    );
    const sendTotalAmount = numberUtils.mathRound(
      sendAmount + sendAnotherAmount,
    );

    const feeAmount = numberUtils.mathRound(
      remittance?.feeAmount?.feeAmount || 0,
    );

    const surchargeAmount =
      paymentMethod && paymentMethod === METHOD_EPAYMENT
        ? isDiscountApplied
          ? numberUtils.mathRound(totalAmountAfterDiscount * SURCHARGE_RATE) ||
            0
          : numberUtils.mathRound(
              numberUtils.mathRound(sendTotalAmount + feeAmount) *
                SURCHARGE_RATE,
            )
        : 0;

    const totalPayAmount = numberUtils.mathRound(
      feeAmount + surchargeAmount + sendTotalAmount,
    );

    const amountLimit = payLimit?.feesIncluded
      ? totalPayAmount
      : sendTotalAmount;

    if (payLimit?.payLimitMax && amountLimit > payLimit?.payLimitMax)
      return {
        ...payLimit,
        amountLimit,
      };

    return false;
  };

  const getNavbarsAndBodyContentOfSidebar = () => {
    return [
      {
        Icon: AddTxnStep1Icon,
        label: t('add_transaction_title_select_receiver_payout_method'),
      },
      {
        Icon: AddTxnStep2Icon,
        label: t('add_transaction_title_remittance'),
      },
      {
        Icon: AddTxnStep3Icon,
        label: t('add_transaction_title_additional_information'),
      },
      {
        Icon: AddTxnStep4Icon,
        label: t('add_transaction_title_receiver_payment'),
      },
    ];
  };

  const renderDiscountFieldLabel = (type, code) => {
    // if (type === VOUCHER)
    //   return (
    //     <>
    //       {t('label_voucher')}{' '}
    //       <span className="discount-code">{`(${code})`}</span>
    //     </>
    //   );
    // return <>{t('label_promotion')}</>;
    if (!code) return '';
    return `${t('label_offer_applied')} <span class="discount-code">(${code})</span>`;
  };

  const getTotalToPay = () => {
    const sendAmount = numberUtils.mathRound(
      remittance[ADD_TRANSACTION_YOU_SEND_NAME] || 0,
    );
    const sendAnotherAmount = numberUtils.mathRound(
      remittance[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0,
    );
    const sendTotalAmount = numberUtils.mathRound(
      sendAmount + sendAnotherAmount,
    );
    const feeAmount = numberUtils.mathRound(
      remittance?.feeAmount?.feeAmount || 0,
    );
    const surchargeAmount =
      paymentMethod?.[ADD_TRANSACTION_PAYMENT_METHOD_NAME] === METHOD_EPAYMENT
        ? isDiscountApplied
          ? numberUtils.mathRound(totalAmountAfterDiscount * SURCHARGE_RATE) ||
            0
          : numberUtils.mathRound(
              numberUtils.mathRound(sendTotalAmount + feeAmount) *
                SURCHARGE_RATE,
            )
        : 0;
    const totalPayAmount = isDiscountApplied
      ? paymentMethod?.[ADD_TRANSACTION_PAYMENT_METHOD_NAME] === METHOD_EPAYMENT
        ? numberUtils.mathRound(
            Number(totalAmountAfterDiscount) +
              Number(totalAmountAfterDiscount) * SURCHARGE_RATE,
          )
        : Number(totalAmountAfterDiscount)
      : numberUtils.mathRound(feeAmount + surchargeAmount + sendTotalAmount);
    return totalPayAmount;
  };

  return {
    getNavbarsAndBodyContentOfSidebar,
    renderDiscountFieldLabel,
    payLimitConfig,
    getPayLimitRanger,
    isPaymentMethodDisabled,
    getTotalToPay,
  };
};

export default useAddTransaction;
